import React, { useContext } from "react";
import axios from "axios";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { useSessionId, ThemeContext, useAuthProvider, saveContentVersion, getContentVersion } from "@cloudpremise/portal_core";
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import DashboardLayout from "@cloudpremise/portal_core/src/themes/mui/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "@cloudpremise/portal_core/src/themes/mui/examples/Navbars/DashboardNavbar";
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ContentVersion = (props) => {
    const MAX_FILE_SIZE = 4500000; //Max file size 4.5 MB 
    const CHUNK_SIZE = 750000; //Chunk Max size 750Kb 
    let defaultState = {
        files: null,
        cancelToken: null,
        contentVersion: null,
        contentVersionId: "",
        contentVersionBody: null,
        contentVersionLiveBody: null
    };

    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);

    function handleChange(event, name){
        setState({
            [name]: event.target.value,
        });
	}

    async function handleFileChange(event, name){
        const file = event.target.files[0];
        let fileContents = await toBase64(file);
        setState({
            [name]: event.target.files,
            contentVersionBody: fileContents
        });
	}

    function toBase64(file){
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    async function handleSubmit(){
        const { files } = state;
        if(files === null || files.length <= 0){
            return;
        }
        const cancelToken = axios.CancelToken.source();
        const file = files[0];
        let fileContents = await toBase64(file);
        setState({
            loading: true,
            cancelToken: cancelToken,
        });
        var base64 = 'base64,';
        if(typeof(fileContents) === "string"){
            var dataStart = fileContents.indexOf(base64) + base64.length;
            fileContents = fileContents.substring(dataStart);
        }
        uploadProcess(file, fileContents);
    }

    function uploadProcess(file, fileContents) {
        if (file.size > MAX_FILE_SIZE) {
            return;
        }
        // set a default size or startpostiton as 0 
        var startPosition = 0;
        // calculate the end size or endPostion using Math.min() function which is return the min. value   
        var endPosition = Math.min(fileContents.length, startPosition + CHUNK_SIZE);

        // start with the initial chunk, and set the attachId(last parameter)is null in begin
        uploadInChunk(file, fileContents, startPosition, endPosition, '');
    }

    function uploadInChunk(file, fileContents, startPosition, endPosition, attachId = '') {
        var getchunk = fileContents.substring(startPosition, endPosition);
        saveContentVersion(file.name, encodeURIComponent(getchunk), attachId, async (result) => {
            attachId = result;
            startPosition = endPosition;
            endPosition = Math.min(fileContents.length, startPosition + CHUNK_SIZE);
            if (startPosition < endPosition) {
                uploadInChunk(file, fileContents, startPosition, endPosition, attachId);
            }else{
                setState({
                    loading: false,
                    cancelToken: null,
                    contentVersion: null,
                    contentVersionId: attachId,
                });
                await handleGetContentVersion(attachId);
            }
        });        
    }

    async function handleGetContentVersion(contentVersionId, downloadFile = false, render = false){
        if(contentVersionId === null || contentVersionId.length <= 0){
            return;
        }
        getContentVersion(contentVersionId, async (data) => {
            const result = JSON.parse(data);
            const byteCharacters = atob(result.VersionData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            var blob = new Blob([byteArray]);
            const fileContents = await toBase64(blob);

            setState({
                loading: false,
                cancelToken: null,
                contentVersion: result,
                contentVersionLiveBody: (render ? fileContents : state.contentVersionLiveBody)
            });

            if(downloadFile){
                download(fileContents, result);
            }
        }); 
        const cancelToken = axios.CancelToken.source();
        setState({
            loading: true,
            cancelToken: cancelToken
        });
    }
    function download(contentVersionLiveBody = null, contentVersion = null){
        if(contentVersionLiveBody === null){
            contentVersionLiveBody = state.contentVersionLiveBody;
        }
        if(contentVersion === null){
            contentVersionLiveBody = state.contentVersion;
        }
        if(contentVersion === null || contentVersionLiveBody === null){
            return handleGetContentVersion(state.contentVersionId, true);
        }
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.href = contentVersionLiveBody;
        a.target = "_blank";
        a.download = contentVersion.Title;
        a.click();
    }
    function isImage(){
        const { contentVersion, files } = state;
        if(contentVersion === null){
            if(files === null){
                return false;
            }
            const file = files[0];
            return (file.type.indexOf("image") !== -1);
        }
        const extension = contentVersion.FileType.toLowerCase();
        if(['jpg', 'jpeg', 'png', 'gif'].indexOf(extension) !== -1){
            return true;
        }
        return false;
    }
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox my={3}>
                <Grid container justifyContent="flex-start" alignItems="flex-start">
                    <Grid item xs={12} md={6} lg={6} mr="auto" position="relative">
                        <MDBox mr={2}>
                            <MDButton
                                component="label"
                                variant="outlined"
                                color="info"
                                tabIndex={-1}
                                role={undefined}
                                startIcon={<FileUploadOutlinedIcon />}
                                sx={{marginRight: "8px"}}
                            >
                                Select Files
                                <VisuallyHiddenInput type="file" onChange={(event) => handleFileChange(event, "files")} />
                            </MDButton>
                            <span className="slds-file-selector__text">
                                {
                                    state.files !== null ?
                                        state.files[0].name
                                    :
                                        ""
                                }
                            </span>
                        </MDBox>
                        <MDButton
                            variant="gradient"
                            color="info"
                            sx={{marginTop: "16px"}}
                            onClick={() => handleSubmit()}
                            disabled={(state.files === null || state.files.length === 0)}
                        >
                            Upload
                        </MDButton>
                        {
                            state.contentVersionBody !== null && isImage() ?
                                <div>
                                    <h3>Loaded from State</h3>
                                    <img src={state.contentVersionBody} alt="attachment" style={{width: "calc(100% - 16px)"}} />
                                </div>
                            :
                            null
                        }
                    </Grid>
                    <Grid item xs={12} md={6} lg={6} mr="auto" position="relative">
                        <MDBox mr={2} mb={2}>
                            <MDInput 
                                label="Content Version Id"
                                onChange={(event) => handleChange(event, "contentVersionId")}
                                value={state.contentVersionId}
                                sx={{width: "100%"}}
                            />
                        </MDBox>
                        <MDBox>
                            <MDButton
                                variant="gradient"
                                color="info"
                                onClick={() => download()}
                                disabled={state.contentVersionId.length === 0 && state.contentVersionLiveBody === null}
                                sx={{marginRight: "16px"}}
                            >
                                Download
                            </MDButton>
                            <MDButton
                                variant="outlined"
                                color="info"
                                onClick={() => handleGetContentVersion(state.contentVersionId, false, true)}
                                disabled={state.contentVersionId.length === 0}
                            >
                                Render
                            </MDButton>
                            {
                                state.contentVersionLiveBody !== null && isImage() ?
                                    <div>
                                        <h3>Loaded from Controller</h3>
                                        <img src={state.contentVersionLiveBody} alt="contenversion" style={{width: "calc(100% - 16px)"}} />
                                    </div>
                                :
                                null
                            }
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    )
};

export default ContentVersion;